import { CUBE_COLORS } from './colors.js';

/**
 * Class representing a Rubik's Cube using the Facelet model
 * A facelet string is 54 characters representing the stickers on the cube
 * Order: Up(0-8), Right(9-17), Front(18-26), Down(27-35), Left(36-44), Back(45-53)
 * Each face is read from top-left to bottom-right
 */
export class FaceletCube {
    constructor(faceletString = null) {
        // If no string provided, create a solved cube
        this.state = faceletString || this.createSolvedState();
        
        if (!this.isValidState(this.state)) {
            throw new Error('Invalid facelet string');
        }
    }

    /**
     * Create a solved cube state
     * @returns {string} Facelet string for a solved cube
     */
    createSolvedState() {
        return 'UUUUUUUUU' +  // Up face (White)
               'RRRRRRRRR' +  // Right face (Red)
               'FFFFFFFFF' +  // Front face (Green)
               'DDDDDDDDD' +  // Down face (Yellow)
               'LLLLLLLLL' +  // Left face (Orange)
               'BBBBBBBBB';   // Back face (Blue)
    }

    /**
     * Validate a facelet string
     * @param {string} state - The facelet string to validate
     * @returns {boolean} Whether the string is valid
     */
    isValidState(state) {
        // Check length
        if (state.length !== 54) return false;

        // Check characters
        const validChars = new Set(['U', 'R', 'F', 'D', 'L', 'B', 'x']);
        if (!state.split('').every(char => validChars.has(char))) return false;

        // Count occurrences of each color (should be 9 each, except U which can have less due to 'x')
        const counts = {};
        for (const char of state) {
            counts[char] = (counts[char] || 0) + 1;
        }
        
        // Check all faces except U have exactly 9 stickers
        if (counts['R'] !== 9 || counts['F'] !== 9 || counts['D'] !== 9 || 
            counts['L'] !== 9 || counts['B'] !== 9) {
            return false;
        }
        
        // Check U + x equals 9
        if ((counts['U'] || 0) + (counts['x'] || 0) !== 9) {
            return false;
        }

        return true;
    }

    /**
     * Get the current facelet string
     * @returns {string} The current facelet string
     */
    getFaceletString() {
        return this.state;
    }

    /**
     * Get a specific facelet
     * @param {number} index - Index in the facelet string (0-53)
     * @returns {string} The facelet color at that position
     */
    getFacelet(index) {
        if (index < 0 || index >= 54) {
            throw new Error('Invalid facelet index');
        }
        return this.state[index];
    }

    /**
     * Get all facelets for a specific face
     * @param {string} face - Face identifier ('U', 'R', 'F', 'D', 'L', 'B')
     * @returns {string} 9 character string representing the face
     */
    getFace(face) {
        const faceIndices = {
            'U': 0,
            'R': 9,
            'F': 18,
            'D': 27,
            'L': 36,
            'B': 45
        };
        const startIndex = faceIndices[face];
        if (startIndex === undefined) {
            throw new Error('Invalid face');
        }
        return this.state.slice(startIndex, startIndex + 9);
    }

    /**
     * Apply a move to the cube
     * @param {string} move - The move to apply (U, U', U2, R, R', etc.)
     */
    applyMove(move) {
        const face = move[0];
        const isPrime = move.includes("'");
        const isDouble = move.includes('2');
        
        // Number of 90-degree clockwise turns
        const turns = isDouble ? 2 : (isPrime ? 3 : 1);
        
        for (let i = 0; i < turns; i++) {
            this.state = this.applyQuarterTurn(face);
        }
    }

    /**
     * Apply a single clockwise quarter turn to a face
     * @param {string} face - The face to turn
     * @returns {string} The new facelet string
     */
    applyQuarterTurn(face) {
        // Define the cycle patterns for each face
        const cycles = {
            'U': [
                [0,2,8,6], [1,5,7,3],  // Face stickers
                [9,18,36,45], [10,19,37,46], [11,20,38,47]  // Edge stickers: R->F->L->B
            ],
            'R': [
                [9,11,17,15], [10,14,16,12],  // Face stickers
                [2,51,29,20], [5,48,32,23], [8,45,35,26]  // Edge stickers: U->B->D->F
            ],
            'F': [
                [18,20,26,24], [19,23,25,21],  // Face stickers
                [6,9,29,44], [7,12,28,41], [8,15,27,38]  // Edge stickers: U->R->D->L
            ],
            'D': [
                [27,29,35,33], [28,32,34,30],  // Face stickers
                [15,51,42,24], [16,52,43,25], [17,53,44,26]  // Edge stickers: R->B->L->F
            ],
            'L': [
                [36,38,44,42], [37,41,43,39],  // Face stickers
                [0,18,27,53], [3,21,30,50], [6,24,33,47]  // Edge stickers: U->F->D->B
            ],
            'B': [
                [45,47,53,51], [46,50,52,48],  // Face stickers
                [2,36,33,17], [1,39,34,14], [0,42,35,11]  // Edge stickers: U->L->D->R
            ]
        };

        let newState = this.state.split('');
        
        // Apply each cycle for the face
        for (const cycle of cycles[face]) {
            // Rotate the stickers in the cycle
            const temp = newState[cycle[0]];
            newState[cycle[0]] = newState[cycle[3]];
            newState[cycle[3]] = newState[cycle[2]];
            newState[cycle[2]] = newState[cycle[1]];
            newState[cycle[1]] = temp;
        }
        
        return newState.join('');
    }

    /**
     * Reset the cube to its solved state
     */
    reset() {
        this.state = this.createSolvedState();
    }
} 